<script setup>
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { ErrorMessage, Field } from "vee-validate";
import { computed, ref } from "vue";

const {
  fieldId,
  fieldName,
  label,
  legend,
  options,
  disabled = false,
  required = false,
  hasEmptyOption = false,
  filteredOptionKey,
  multipleFilteredOptionKeys,
  displayValue,
  multiple = false,
  inlineLabel = false,
  size = "medium",
  by = null,
} = defineProps({
  fieldId: {
    type: String,
    required: true,
  },
  fieldName: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  legend: {
    type: String,
    required: false,
  },
  options: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  required: {
    type: Boolean,
    required: false,
  },
  hasEmptyOption: {
    type: Boolean,
    required: false,
  },
  filteredOptionKey: {
    type: String,
    required: true,
  },
  multipleFilteredOptionKeys: {
    type: Array,
    required: false,
  },
  displayValue: {
    type: String,
    required: false,
  },
  multiple: {
    type: Boolean,
    required: false,
  },
  inlineLabel: {
    type: Boolean,
    required: false,
  },
  size: {
    type: String,
    required: false,
  },
  by: {
    type: String,
    required: false,
  },
});

const model = defineModel();

const query = ref("");

function normalizeQuery(query) {
  return query
    .toString()
    .trim()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .normalize("NFC")
    .toLocaleLowerCase();
}

const filteredOptions = computed(() => {
  return options.filter((option) => {
    if (multipleFilteredOptionKeys) {
      return multipleFilteredOptionKeys.some((key) => {
        return normalizeQuery(option[key]).includes(
          normalizeQuery(query.value),
        );
      });
    }

    return normalizeQuery(option[filteredOptionKey]).includes(
      normalizeQuery(query.value),
    );
  });
});

const removeSelectedOptionByIndex = (index) => {
  model.value.splice(index, 1);
};

const computedProps = computed(() => {
  let tmpProps = {};

  if (by) {
    tmpProps.by = by;
  }

  return tmpProps;
});
</script>

<template>
  <div
    class="mb-4 flex"
    :class="{
      'flex-row items-center': inlineLabel,
      'flex-col': !inlineLabel,
    }"
  >
    <label
      v-if="label"
      :for="fieldName"
      class="whitespace-nowrap"
      :class="{
        'mr-2': inlineLabel,
        'mb-2': !inlineLabel,
        'text-sm': size === 'small',
      }"
    >
      {{ label }}
      <sup v-if="required" class="text-red-500">*</sup>
    </label>

    <ul v-if="multiple && model" class="mb-3 flex min-h-8 flex-wrap gap-2">
      <li
        v-for="(item, index) in model"
        :key="index"
        class="flex items-center gap-2 rounded px-3 py-1 last:mr-0"
        :class="{
          'bg-gray-200': !disabled,
          'bg-gray-100': disabled,
        }"
      >
        {{ item[filteredOptionKey] }}

        <button
          type="button"
          class="disabled:cursor-not-allowed"
          :disabled="disabled"
          @click="removeSelectedOptionByIndex(index)"
        >
          <XMarkIcon class="size-4" />
        </button>
      </li>
    </ul>

    <Field :id="fieldId" :name="fieldName" v-model="model">
      <div class="relative">
        <Combobox
          v-slot="{ open }"
          v-model="model"
          :disabled="disabled"
          v-bind="computedProps"
          :multiple="multiple"
        >
          <div
            class="flex w-full items-center rounded disabled:cursor-not-allowed disabled:bg-gray-100"
          >
            <ComboboxInput
              class="z-10 -mr-1 w-full rounded-l border border-gray-300 ring-1 duration-150 placeholder:text-gray-400 focus:outline-0 focus:ring-1 focus:ring-gray-300"
              :class="{
                'ring-gray-300': open,
                'ring-transparent': !open,
                'min-w-[220px] px-5 py-3': size === 'medium',
                'h-[28px] min-w-[160px] px-3 py-1 text-sm': size === 'small',
                'cursor-not-allowed bg-gray-100 focus:!ring-0': disabled,
              }"
              :displayValue="
                (value) =>
                  multiple
                    ? null
                    : displayValue
                      ? displayValue
                      : value
                        ? value[filteredOptionKey]
                        : ''
              "
              @change="query = $event.target.value"
              :disabled="disabled"
            />

            <ComboboxButton
              class="rounded-r border border-gray-300 outline outline-1 focus:outline-gray-300"
              :class="{
                'outline-gray-300': open,
                'outline-transparent': !open,
                'h-[50px] px-5 py-3': size === 'medium',
                'h-[28px] px-3 py-1': size === 'small',
                'focus:!outline-0 disabled:cursor-not-allowed disabled:bg-gray-100':
                  disabled,
              }"
            >
              <ChevronDownIcon
                class="size-4 text-gray-500 duration-150"
                :class="{
                  'rotate-180 transform': open,
                }"
              />
            </ComboboxButton>
          </div>

          <div
            class="absolute left-0 z-50 h-[17.5rem] w-full"
            :class="{
              hidden: !open,
              'top-14': size === 'medium',
              'top-8': size === 'small',
            }"
          >
            <ComboboxOptions
              class="max-h-[15rem] overflow-y-auto rounded border border-gray-300 bg-white shadow"
            >
              <ComboboxOption
                v-if="hasEmptyOption && !multiple"
                v-slot="{ selected, active }"
                :value="null"
                as="template"
              >
                <li
                  class="flex cursor-pointer items-center duration-150 hover:bg-indigo-600"
                  :class="{
                    'bg-indigo-600 text-white':
                      active || selected || modelValue === null,
                    'min-h-[40px] px-5 py-2': size === 'medium',
                    'h-[28px] px-3 py-1 text-sm': size === 'small',
                  }"
                >
                  &nbsp;
                </li>
              </ComboboxOption>

              <ComboboxOption
                v-slot="{ selected, active }"
                v-for="(option, index) in filteredOptions"
                :key="index"
                :value="option"
                as="template"
              >
                <li
                  class="cursor-pointer truncate duration-150 hover:bg-indigo-600 hover:text-white"
                  :class="{
                    'bg-indigo-600 text-white': active || selected,
                    'min-h-[40px] px-5 py-2': size === 'medium',
                    'h-[28px] px-3 py-1 text-sm': size === 'small',
                  }"
                >
                  <slot name="optionContent" :option="option" />
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </div>
        </Combobox>
      </div>
    </Field>

    <em v-if="legend" class="mt-1 text-xs text-gray-400">
      {{ legend }}
    </em>

    <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
  </div>
</template>
